import React, { useEffect, useState } from "react";
import { Button, Card, Col, Collapse, Form, Row } from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { CHEFS_FARM, COMPANY_CONSTANTS, GROWY, GROWY_COMPANY_CONSTANTS } from "../../../../constants/CompanyConstants";
import ContactField from "./ContactField";
import CompanyField from "./CompanyField";

const AVAILABLE_COMPANIES = [
  { code: CHEFS_FARM, label: "Chef's Farm" },
  { code: GROWY, label: "Growy" },
];

const COMPANY_DEFAULTS = {
  [CHEFS_FARM]: {
    contact: {
      copyright: "Copyright © 2024 Chef's Farm B.V.",
      email: COMPANY_CONSTANTS.EMAIL,
      emailLink: `mailto:${COMPANY_CONSTANTS.EMAIL}`,
      emailSupport: COMPANY_CONSTANTS.EMAIL,
      googleMapsLink: "https://maps.app.goo.gl/oJCyN6coZaxeVjNJ7",
      phone: COMPANY_CONSTANTS.PHONE,
      phoneLink: `tel:${COMPANY_CONSTANTS.PHONE.replace(/\s/g, "")}`,
      privacyPolicyLink: "https://www.chefsfarm.nl/privacy-policy/",
      termsLink: "https://www.chefsfarm.nl/privacy-policy/",
      url: "https://www.chefsfarm.nl/",
      whatsappLink: `https://wa.me/${COMPANY_CONSTANTS.PHONE.replace(/\s/g, "")}`,
    },
    company: {
      name: COMPANY_CONSTANTS.COMPANY_NAME,
      street: COMPANY_CONSTANTS.STREET_AND_NUMBER,
      zipCity: COMPANY_CONSTANTS.ZIP_AND_CITY,
      country: COMPANY_CONSTANTS.COUNTRY,
      kvk: COMPANY_CONSTANTS.KVK,
      vat: COMPANY_CONSTANTS.VAT,
    },
  },
  [GROWY]: {
    contact: {
      copyright: "Copyright © 2024 Growy Nederland BV",
      email: GROWY_COMPANY_CONSTANTS.EMAIL,
      emailLink: `mailto:${GROWY_COMPANY_CONSTANTS.EMAIL}`,
      emailSupport: GROWY_COMPANY_CONSTANTS.EMAIL,
      phone: GROWY_COMPANY_CONSTANTS.PHONE,
      phoneLink: `tel:${GROWY_COMPANY_CONSTANTS.PHONE.replace(/\s/g, "")}`,
      url: "https://www.growy.nl/",
      whatsappLink: `https://wa.me/${GROWY_COMPANY_CONSTANTS.PHONE.replace(/\s/g, "")}`,
    },
    company: {
      name: GROWY_COMPANY_CONSTANTS.COMPANY_NAME,
      street: GROWY_COMPANY_CONSTANTS.STREET_AND_NUMBER,
      zipCity: GROWY_COMPANY_CONSTANTS.ZIP_AND_CITY,
      country: GROWY_COMPANY_CONSTANTS.COUNTRY,
      kvk: GROWY_COMPANY_CONSTANTS.KVK,
      vat: GROWY_COMPANY_CONSTANTS.VAT,
    },
  },
};

const CompaniesField = ({ mode }) => {
  const { t } = useTranslation();
  const form = Form.useFormInstance();
  const isViewMode = mode === "VIEW";
  const [refreshKey, setRefreshKey] = useState(0);
  const [activeCodes, setActiveCodes] = useState([]);

  // Update activeCodes whenever refreshKey changes or form values change
  useEffect(() => {
    const companiesValue = form.getFieldValue("companies") || {};
    const codes = Object.keys(companiesValue)
      .filter((key) => companiesValue[key] !== undefined)
      .sort();
    setActiveCodes(codes);
  }, [refreshKey, form]);

  const availableToAdd = AVAILABLE_COMPANIES.filter((c) => !activeCodes.includes(c.code));

  const handleAddCompany = (code) => {
    const defaults = COMPANY_DEFAULTS[code] || { contact: {}, company: {} };
    const current = form.getFieldValue("companies") || {};
    const updated = { ...current, [code]: defaults };
    form.setFieldsValue({ companies: updated });
    setRefreshKey((prev) => prev + 1);
  };

  const handleRemoveCompany = (code) => {
    const current = form.getFieldValue("companies") || {};
    const updated = { ...current, [code]: undefined };
    form.setFieldsValue({ companies: updated });
    setRefreshKey((prev) => prev + 1);
  };

  return (
    <Row gutter={[16, 16]} key={refreshKey}>
      {activeCodes.map((code) => {
        const companyLabel = AVAILABLE_COMPANIES.find((c) => c.code === code)?.label || code;
        return (
          <Col span={24} key={code}>
            <Card
              title={
                <span>
                  {code} ({companyLabel})
                </span>
              }
              extra={
                !isViewMode ? (
                  <Button type="text" danger icon={<DeleteOutlined />} onClick={() => handleRemoveCompany(code)}>
                    {t("remove")}
                  </Button>
                ) : null
              }
            >
              <Collapse ghost>
                <Collapse.Panel header={t("countries.contact_section")} key="contact">
                  <ContactField mode={mode} companyCode={code} />
                </Collapse.Panel>
                <Collapse.Panel header={t("countries.company_section")} key="company">
                  <CompanyField mode={mode} companyCode={code} />
                </Collapse.Panel>
              </Collapse>
            </Card>
          </Col>
        );
      })}

      {!isViewMode && availableToAdd.length > 0 && (
        <Col span={24}>
          {availableToAdd.map((company) => (
            <Button
              key={company.code}
              type="dashed"
              icon={<PlusOutlined />}
              onClick={() => handleAddCompany(company.code)}
              style={{ width: "100%", marginBottom: 8 }}
            >
              {t("countries.add_company")}: {company.label} ({company.code})
            </Button>
          ))}
        </Col>
      )}
    </Row>
  );
};

export default CompaniesField;
